import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

/**
 * OnboardingGate — wraps the authenticated Layout so eligible customer
 * users of a company with unfinished onboarding are nudged to
 * `/onboarding`. Eligibility itself is computed on the backend
 * (see /api/onboarding/state → `eligible`) so the client stays
 * dumb: it just honours the signal it receives.
 *
 * Rules the backend enforces:
 *   - Super Admin and unattached accounts → `eligible=false` (never
 *     redirected; they still have their own admin tools).
 *   - Company Admin (`role=corporate`) → always eligible when the
 *     company's onboarding is not yet completed.
 *   - Restaurant Admin → eligible ONLY when the company has no
 *     Company Admin assigned yet, so small tenants can still finish
 *     the initial setup.
 *   - Every operational role (head_chef, kitchen_staff, read_only, …)
 *     → `eligible=false`.
 *
 * We issue exactly one GET /api/onboarding/state per session-mount and
 * never block render — a transient error is logged and the user
 * proceeds normally (they can re-visit /onboarding manually).
 */
export default function OnboardingGate() {
    const { user } = useApp();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    useEffect(() => {
        if (!user) return;
        if (pathname.startsWith("/onboarding")) return;
        // Never fight LegalGate — while the user is completing legal
        // acceptance, the onboarding redirect must stand down.
        if (pathname.startsWith("/legal")) return;
        // Cheap client-side pre-filter: Super Admin never gets auto-
        // redirected (they'll get `eligible=false` from the backend
        // anyway, but skipping the network call avoids noise in the
        // platform-admin flows).
        if (user.role === "super_admin") return;
        let cancelled = false;
        api.get("/onboarding/state").then(({ data }) => {
            if (cancelled) return;
            if (!data?.eligible) return;
            if (data.onboarding_status === "pending"
                || data.onboarding_status === "in_progress") {
                navigate("/onboarding", { replace: true });
            }
        }).catch((err) => {
            // Never block render on this lookup. Log so support can
            // trace missed redirects from a session recording.
            console.warn("[OnboardingGate] state lookup failed", err?.message || err);
        });
        return () => { cancelled = true; };
    }, [user, pathname, navigate]);

    return <Outlet />;
}
